import { useAuthenticationStore } from "@/stores/authentication";
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import GeolocationService from "./GeolocationService";
import { useGeolocationStore } from "@/stores/geolocation";
import { removeUser } from '@/services/StorageService';

enum StatusCode {
    Unauthorized = 401,
    Forbidden = 403,
    TooManyRequests = 429,
    InternalServerError = 500,
}

const headers: Readonly<Record<string, string | boolean>> = {
    Accept: "application/json",
    "Content-Type": "application/json; charset=utf-8",
    //"Access-Control-Allow-Credentials": true,
    "X-Requested-With": "XMLHttpRequest",
};

// We can use the following function to inject the JWT token through an interceptor
// We get the `accessToken` from the localStorage that we set when we authenticate
// const injectToken = (config: AxiosRequestConfig): AxiosRequestConfig => {
//     try {
//         const token = localStorage.getItem("accessToken");

//         if (token != null && config && config.headers) {
//             config.headers.Authorization = `Bearer ${token}`;
//         }
//         return config;
//     } catch (error: any) {
//         throw new Error(error);
//     }
// };

class Http {
    private instance: AxiosInstance | null = null;
    private authenticationStore: any = null;
    private geolocationStore: any = null;

    public get http(): AxiosInstance {
        return this.instance != null ? this.instance : this.initHttp();
    }

    initHttp() {
        const http = axios.create({
            baseURL: process.env.VUE_APP_API_BASE_URL,
            headers
        });

        http.interceptors.response.use(
            (response) => response.data,
            (error) => {
                const { response } = error;
                return this.handleError(response);
            }
        );

        this.instance = http;
        this.authenticationStore = useAuthenticationStore();
        this.geolocationStore = useGeolocationStore();
        return http;
    }

    request<T = any, R = AxiosResponse<T>>(config: AxiosRequestConfig): Promise<R> {
        return this.http.request(config);
    }

    get<T = any, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
        return this.http.get<T, R>(url, config);
    }

    post<T = any, R = AxiosResponse<T>>(
        url: string,
        data?: T,
        config?: AxiosRequestConfig
    ): Promise<R> {
        return this.http.post<T, R>(url, data, config);
    }

    put<T = any, R = AxiosResponse<T>>(
        url: string,
        data?: T,
        config?: AxiosRequestConfig
    ): Promise<R> {
        return this.http.put<T, R>(url, data, config);
    }

    delete<T = any, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
        return this.http.delete<T, R>(url, config);
    }

    // Handle global app errors
    // We can handle generic app errors depending on the status code
    private handleError(error: any) {
        const { status } = error;

        switch (status) {
            case StatusCode.InternalServerError: {
                // Handle InternalServerError
                break;
            }
            case StatusCode.Forbidden: {
                // Handle Forbidden
                removeUser();
                this.authenticationStore.removeUser();
                if (this.geolocationStore.isLocationLive) {
                    GeolocationService.endSendingGeolocation();
                }
                window.location.href = '/login';
                break;
            }
            case StatusCode.Unauthorized: {
                // Handle Unauthorized
                break;
            }
            case StatusCode.TooManyRequests: {
                // Handle TooManyRequests
                break;
            }
        }

        return Promise.reject(error);
    }
}

export const http = new Http();